// This API client should only be used server-side due to its use of secrets.
import { applyClientOriginHeader } from "./client-origin";

interface ApiOptions {
  apiKey?: string;
}

/**
 * Replaces path parameters in a URL with actual values.
 * e.g. /{id} becomes /123 if id is 123.
 */
export function replacePathParam(
  url: string,
  paramName: string,
  paramValue: string,
): string {
  // Matches {paramName} patterns
  const pathParamRegex = new RegExp(`{${paramName}}`, "g");
  return url.replace(pathParamRegex, paramValue);
}

/**
 * Fetches data from an API endpoint
 *
 * Sets Content-Type to application/json for non-GET requests
 * Adds x-functions-key header if apiKey is provided
 * Handles errors by throwing an Error with status and message
 * Returns JSON response or text if JSON parsing fails
 */
export async function apiFetch<T>(
  url: string,
  options: RequestInit = {},
  { apiKey }: ApiOptions = {},
): Promise<T> {
  const method = options.method || "GET";

  const mergedHeaders = applyClientOriginHeader(options.headers);

  if (!mergedHeaders.has("Content-Type") && method !== "GET") {
    mergedHeaders.set("Content-Type", "application/json");
  }

  if (apiKey) {
    mergedHeaders.set("x-functions-key", apiKey);
  }

  const response = await globalThis.fetch(url, {
    ...options,
    headers: mergedHeaders,
  });

  if (!response.ok) {
    let errorMessage = `API error: ${response.status}`;

    try {
      const errorData = await response.json();
      errorMessage += ` - ${JSON.stringify(errorData)}`;
    } catch {
      // If JSON parsing fails, try to get text response
      try {
        const errorText = await response.text();
        errorMessage += ` - ${errorText}`;
      } catch {
        // If both fail, use status text
        errorMessage += ` - ${response.statusText}`;
      }
    }

    throw new Error(errorMessage);
  }

  // The global opportunities form returns text/plain instead of application/json
  const contentType = response.headers.get("content-type");

  if (contentType && contentType.includes("application/json")) {
    return response.json();
  } else {
    const text = await response.text();

    if (text === "Success") {
      return { success: true } as unknown as T;
    }

    try {
      return JSON.parse(text);
    } catch {
      return { success: true, text } as unknown as T;
    }
  }
}

/**
 * Fetches data using GET method
 */
export async function apiGet<T>(
  url: string,
  options: RequestInit = {},
  apiOptions: ApiOptions = {},
): Promise<T> {
  return apiFetch<T>(url, { ...options, method: "GET" }, apiOptions);
}

/**
 * Fetches data using POST method
 */
export async function apiPost<T>(
  url: string,
  options: RequestInit = {},
  apiOptions: ApiOptions = {},
): Promise<T> {
  return apiFetch<T>(
    url,
    {
      ...options,
      method: "POST",
    },
    apiOptions,
  );
}
